import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { buildApiUrl } from './config';

const HomeworkResult = ({ onBack }) => {
  const { studentId, homeworkId } = useParams();
  const navigate = useNavigate();

  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [explanations, setExplanations] = useState({});
  const [loadingIndex, setLoadingIndex] = useState(null);

  const norm = (v) =>
    Array.isArray(v)
      ? v.map((x) => String(x).trim().toLowerCase())
      : String(v).trim().toLowerCase();

  useEffect(() => {
    if (!studentId || !homeworkId) return;
    console.log("📝 Fetching homework result for:", { studentId, homeworkId });
    fetch(`${buildApiUrl("")}/homework-result/${studentId}/${homeworkId}`)
      .then((res) => res.json())
      .then((data) => {
        console.log("📊 Homework result received:", data);
        const raw = data.result;
        if (raw) {
          console.log("   Score:", raw.score, "/", raw.total);
          console.log("   Answers count:", raw.answers?.length);
          console.log("   First answer sample:", raw.answers?.[0]);
          setScore(raw.score || 0);
          setTotal(raw.total || 0);
          setAnswers(raw.answers || []);
          setLoaded(true);
        } else {
          console.warn("⚠️ No result data found");
          setLoaded(true);
        }
      })
      .catch((err) => {
        setLoaded(true);
        console.error("❌ Failed to fetch homework result:", err);
      });
  }, [studentId, homeworkId]);

  const fetchExplanation = (answer, index) => {
    setLoadingIndex(index);
    fetch(`${buildApiUrl("")}/explain-answer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question: answer.label,
        correct: answer.correct,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setExplanations((prev) => ({
          ...prev,
          [index]: data.explanation,
        }));
      })
      .catch((err) => {
        setExplanations((prev) => ({
          ...prev,
          [index]: "Failed to load explanation.",
        }));
      })
      .finally(() => setLoadingIndex(null));
  };

  const getScoreColor = () => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = () => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return "bg-green-50 border-green-200";
    if (percentage >= 60) return "bg-yellow-50 border-yellow-200";
    return "bg-red-50 border-red-200";
  };

  const getScoreIcon = () => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return "🎉";
    if (percentage >= 60) return "👍";
    return "📚";
  };

  if (!loaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="text-gray-600 text-lg">Loading your results...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">{getScoreIcon()}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Homework Completed!</h1>
          <p className="text-gray-600">Here are your results and detailed feedback</p>
        </div>

        {/* Score Card */}
        <div className={`rounded-2xl border-2 p-8 mb-8 text-center ${getScoreBgColor()}`}>
          <div className="mb-4">
            <div className={`text-6xl font-bold ${getScoreColor()}`}>
              {score}/{total}
            </div>
            <div className="text-lg text-gray-600 mt-2">
              {total > 0 ? Math.round((score / total) * 100) : 0}% Correct
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className={`h-3 rounded-full transition-all duration-1000 ease-out ${
                (score / total) >= 0.8 ? 'bg-green-500' : 
                (score / total) >= 0.6 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${total > 0 ? (score / total) * 100 : 0}%` }}
            ></div>
          </div>

          <div className="text-sm text-gray-600">
            {(score / total) >= 0.8 ? "Excellent work! 🌟" : 
             (score / total) >= 0.6 ? "Good job! Keep practicing 💪" : 
             "Keep studying and you'll improve! 📖"}
          </div>
        </div>

        {/* Questions Review */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 text-lg">📝</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Question Review</h2>
          </div>

          {answers.map((q, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                {/* Question Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{q.label}</h3>
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-2 mb-4">
                  {q.options.map((opt, i) => {
                    const isCorrect = Array.isArray(q.correct)
                      ? norm(q.correct).includes(norm(opt))
                      : norm(opt) === norm(q.correct);

                    const isSelected = Array.isArray(q.selected)
                      ? norm(q.selected).includes(norm(opt))
                      : norm(opt) === norm(q.selected);

                    const wrongSelected = isSelected && !isCorrect;

                    return (
                      <div
                        key={i}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-200 ${
                          isCorrect
                            ? "bg-green-50 border-green-200 text-green-800"
                            : wrongSelected
                            ? "bg-red-50 border-red-200 text-red-800"
                            : "bg-gray-50 border-gray-200 text-gray-700"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            isCorrect ? "bg-green-500" : wrongSelected ? "bg-red-500" : "bg-gray-300"
                          }`}>
                            {isCorrect ? (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : wrongSelected ? (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            ) : null}
                          </div>
                          <span className="font-medium">{opt}</span>
                        </div>
                        
                        {isCorrect && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Correct Answer
                          </span>
                        )}
                        {wrongSelected && (
                          <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                            Your Answer
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Answer Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="text-sm text-gray-600">
                    <strong>Your answer:</strong> {
                      typeof q.selected !== "undefined" && q.selected !== null
                        ? Array.isArray(q.selected) ? q.selected.join(", ") : q.selected
                        : "No answer selected"
                    }
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    <strong>Correct answer:</strong> {
                      Array.isArray(q.correct) ? q.correct.join(", ") : q.correct || "N/A"
                    }
                  </div>
                </div>

                {/* Explanation Section */}
                <div className="border-t border-gray-200 pt-4">
                  {loadingIndex === index ? (
                    <div className="flex items-center gap-3 text-blue-600">
                      <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                      <span className="text-sm">Loading explanation...</span>
                    </div>
                  ) : explanations[index] ? (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 text-sm">💡</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-blue-900 mb-1">Explanation:</h4>
                          <p className="text-blue-800 text-sm">{explanations[index]}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium"
                      onClick={() => fetchExplanation(q, index)}
                    >
                      <span>💡</span>
                      Get Explanation
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <button
            className="px-8 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors duration-200 font-medium"
            onClick={() => {
              if (onBack) return onBack();
              navigate(`/student-dashboard`, { state: { activeTab: 1 } });
            }}
          >
            ← Back to Dashboard
          </button>
          <button
            className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors duration-200 font-medium"
            onClick={() => window.print()}
          >
            📄 Print Results
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeworkResult;
